import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AppContext = createContext(null);
const TENANT_STORAGE_SCOPE = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : 'server';
const CHECKOUT_STORAGE_KEY = `checkoutDeliveryState:${TENANT_STORAGE_SCOPE}`;

export const AppProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      // Check inactivity before loading cart
      const lastActivity = parseInt(localStorage.getItem('mj_lastActivity') || Date.now());
      const now = Date.now();
      const INACTIVITY_TIMEOUT = 45 * 60 * 1000; // 45 minutes
      
      if (now - lastActivity > INACTIVITY_TIMEOUT) {
        // Clear cart if inactive for >45 mins
        localStorage.removeItem('mj_cart');
        localStorage.removeItem('mj_lastActivity');
        return [];
      }
      
      return JSON.parse(localStorage.getItem('mj_cart') || '[]');
    } catch {
      return [];
    }
  });

 const [orderType, setOrderType] = useState(() => {
  try {
    const savedCheckoutState = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);

    if (savedCheckoutState) {
      const parsed = JSON.parse(savedCheckoutState);
      if (parsed?.activeTab) {
        return parsed.activeTab; // 🔥 delivery or pickup from checkout
      }
    }

    return localStorage.getItem('mj_orderType') || 'delivery';
  } catch {
    return 'delivery';
  }
});

  // Authentication is restored from the server, not trusted from local storage.
  // This prevents an old mj_isAdmin flag from exposing admin UI to a customer.
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [currentOrder, setCurrentOrder] = useState(null);
  
  // Branding state
  const [branding, setBranding] = useState({
    primaryColor: '#f59e0b',
    secondaryColor: '#d97706',
    logoUrl: null
  });

  const [promotions, setPromotions] = useState([]);
  const [selectedPromotion, setSelectedPromotion] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      const token = localStorage.getItem('mj_token');
      if (!token) {
        localStorage.removeItem('mj_user');
        localStorage.removeItem('mj_isAdmin');
        if (!cancelled) setAuthLoading(false);
        return;
      }

      try {
        const currentUser = await api.getMe();
        if (cancelled) return;

        const currentIsAdmin = currentUser?.isAdmin === true;
        setUser(currentUser);
        setIsAdmin(currentIsAdmin);
        localStorage.setItem('mj_user', JSON.stringify(currentUser));

        if (currentIsAdmin) {
          localStorage.setItem('mj_isAdmin', 'true');
        } else {
          localStorage.removeItem('mj_isAdmin');
        }
      } catch (error) {
        if (cancelled) return;
        setUser(null);
        setIsAdmin(false);
        localStorage.removeItem('mj_token');
        localStorage.removeItem('mj_user');
        localStorage.removeItem('mj_isAdmin');
      } finally {
        if (!cancelled) setAuthLoading(false);
      }
    };

    restoreSession();
    return () => { cancelled = true; };
  }, []);

  // Load branding on mount
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const restaurant = await api.getRestaurant();
        const colors = {
          primaryColor: restaurant.primaryColor || '#f59e0b',
          secondaryColor: restaurant.secondaryColor || '#d97706',
          logoUrl: restaurant.logoUrl || null
        };
        setBranding(colors);
        
        // Apply CSS variables to root
        document.documentElement.style.setProperty('--primary-color', colors.primaryColor);
        document.documentElement.style.setProperty('--secondary-color', colors.secondaryColor);
      } catch (err) {
        console.error('Failed to load branding:', err);
      }
    };
    loadBranding();
  }, []);

  useEffect(() => {
  api.getPromotions()
    .then(setPromotions)
    .catch(() => setPromotions([]));
}, []);

  // Cart inactivity timer (45 mins)
  useEffect(() => {
    const INACTIVITY_TIMEOUT = 45 * 60 * 1000; // 45 minutes in milliseconds
    
    const updateActivity = () => {
      localStorage.setItem('mj_lastActivity', Date.now().toString());
    };
    
    const checkInactivity = () => {
      const lastActivity = parseInt(localStorage.getItem('mj_lastActivity') || Date.now());
      const now = Date.now();
      
      if (now - lastActivity > INACTIVITY_TIMEOUT && cart.length > 0) {
        // Clear cart silently
        setCart([]);
        localStorage.removeItem('mj_cart');
        localStorage.removeItem('mj_lastActivity');
      }
    };
    
    // Set initial activity time if not exists
    if (!localStorage.getItem('mj_lastActivity')) {
      updateActivity();
    }
    
    // Check on mount
    checkInactivity();
    
    // Check every minute
    const interval = setInterval(checkInactivity, 60000);
    
    // Update activity on user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });
    
    return () => {
      clearInterval(interval);
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [cart.length]); // Only depend on cart length, not cart itself

  useEffect(() => {
    localStorage.setItem('mj_cart', JSON.stringify(cart));
  }, [cart]);

  // A category URL may be restored from the browser's back/forward cache.
  // Reconcile the in-memory basket with localStorage when that happens so the
  // user never has to refresh to see an item added on another menu history entry.
  useEffect(() => {
    const syncCartFromStorage = () => {
      try {
        const storedCart = JSON.parse(localStorage.getItem('mj_cart') || '[]');
        setCart(current => JSON.stringify(current) === JSON.stringify(storedCart) ? current : storedCart);
      } catch (_) {
        // Keep the current basket if stored data is temporarily unreadable.
      }
    };

    window.addEventListener('pageshow', syncCartFromStorage);
    window.addEventListener('focus', syncCartFromStorage);
    window.addEventListener('storage', syncCartFromStorage);
    return () => {
      window.removeEventListener('pageshow', syncCartFromStorage);
      window.removeEventListener('focus', syncCartFromStorage);
      window.removeEventListener('storage', syncCartFromStorage);
    };
  }, []);

  useEffect(() => {
    if (orderType) localStorage.setItem('mj_orderType', orderType);
  }, [orderType]);

  useEffect(() => {
  try {
    const savedCheckoutState = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (!savedCheckoutState) return;

    const parsed = JSON.parse(savedCheckoutState);

    if (parsed?.activeTab && parsed.activeTab !== orderType) {
      setOrderType(parsed.activeTab);
    }
  } catch (e) {
    console.error('Failed syncing orderType from checkout', e);
  }
}, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem('mj_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('mj_user');
      localStorage.removeItem('mj_isAdmin');
    }
  }, [user]);

  const addToCart = (item, quantity = 1, selectedOptions = {}) => {
    const optionsKey = JSON.stringify(selectedOptions);
    const cartItemId = `${item.id}_${optionsKey}`;
    if (item.promotionReward) {

      quantity = 1;

      setCart(prev =>
        prev.filter(ci => !ci.promotionReward)
      );
    }
    setCart(prev => {
      const existing = prev.find(ci => ci.cartItemId === cartItemId);
      if (existing) {
        return prev.map(ci =>
          ci.cartItemId === cartItemId
            ? { ...ci, quantity: ci.quantity + quantity }
            : ci
        );
      }
      const optionsPrice = Object.values(selectedOptions).reduce((sum, choice) => {
        if (Array.isArray(choice)) {
          return sum + choice.reduce((s, c) => s + (c.price || 0), 0);
        }
        return sum + (choice?.price || 0);
      }, 0);
      return [...prev, {
      cartItemId,
      id: item.id,
      categoryId: item.categoryId || null,
      name: item.name,
      price: item.price + optionsPrice,
      basePrice: item.price,
      image: item.image,
      quantity,
      selectedOptions,
      promotionReward: item.promotionReward || false,
      promotionId: item.promotionId || null,
      rewardId: item.rewardId || null
    }];
    });
  };

  const removeFromCart = (cartItemId) => {
    setCart(prev => prev.filter(ci => ci.cartItemId !== cartItemId));
  };

  const updateQuantity = (cartItemId, quantity) => {

  const existingItem = cart.find(
    ci => ci.cartItemId === cartItemId
  );

  if (existingItem?.promotionReward) {
    return;
  }

  if (quantity <= 0) {
    removeFromCart(cartItemId);
    return;
  }

  setCart(prev =>
    prev.map(ci =>
      ci.cartItemId === cartItemId
        ? { ...ci, quantity }
        : ci
    )
  );
};

  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const cartSubtotal = cart.reduce((sum, i) => sum + (i.price * i.quantity), 0);

  const getPromotionEligibleSpend = (promotion) => {
  if (!promotion) {
    return 0;
  }

  const excludedCategoryIds =
    promotion.excludedCategoryIds || [];

  const excludedMenuItemIds =
    promotion.excludedMenuItemIds || [];

    return cart
      .filter(item => !item.promotionReward)
      .filter(
        item =>
          !excludedMenuItemIds.includes(item.id)
      )
      .filter(
        item =>
          !excludedCategoryIds.includes(item.categoryId)
      )
      .reduce(
        (sum, item) =>
          sum + item.price * item.quantity,
        0
      );
  };

useEffect(() => {
  const promoRewardItem = cart.find(
    item => item.promotionReward
  );

  if (!promoRewardItem) {
    return;
  }

  const matchingPromotion =
    promotions.find(
      promo =>
        promo.id === promoRewardItem.promotionId
    ) ||
    promotions.find(
      promo =>
        promo.rewardItems?.some(
          reward => reward.id === promoRewardItem.id
        )
    );

  if (!matchingPromotion) {
    setCart(currentCart =>
      currentCart.filter(
        item => !item.promotionReward
      )
    );

    setSelectedPromotion(null);
    return;
  }

  const eligibleSpend =
    getPromotionEligibleSpend(
      matchingPromotion
    );

  if (
    eligibleSpend <
    (matchingPromotion.minSpend || 0)
  ) {
    setCart(currentCart =>
      currentCart.filter(
        item => !item.promotionReward
      )
    );

    setSelectedPromotion(null);
  }
}, [cart, promotions]);

  const login = (userData) => {
    setUser(userData);
    const nextIsAdmin = userData?.isAdmin === true;
    setIsAdmin(nextIsAdmin);
    if (nextIsAdmin) {
      localStorage.setItem('mj_isAdmin', 'true');
    } else {
      localStorage.removeItem('mj_isAdmin');
    }
  };

  const logout = () => {
    setUser(null);
    setIsAdmin(false);
    localStorage.removeItem('mj_token');
    localStorage.removeItem('mj_user');
    localStorage.removeItem('mj_isAdmin');
  };

  const activePromotions = promotions.filter(
  p => p.enabled !== false
);

  return (
    <AppContext.Provider value={{
      cart, addToCart, removeFromCart, updateQuantity, clearCart,
      cartCount, 
      cartSubtotal,
      getPromotionEligibleSpend,
      orderType, setOrderType,
      user, setUser, login, logout, isAdmin, authLoading,
      currentOrder, setCurrentOrder,
      branding,
      promotions,
      activePromotions,
      setPromotions,
      selectedPromotion,
      setSelectedPromotion,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
